import { useState } from 'react';
import { disclaimerAPI } from '@/services/api';
import { useStore, useIsAuthenticated, useBetaDisclaimer } from '@/stores';
import { BetaDisclaimerModal } from './BetaDisclaimerModal';

/**
 * Blocks the authenticated app until the user accepts the current beta
 * disclaimer.
 *
 * Acceptance state arrives on the login/me payloads and lives in the auth slice
 * (`betaDisclaimer`). When the stored version is unaccepted, this renders an
 * undismissable modal; accepting records it on the backend and updates the
 * stored state in place so the gate closes.
 */
export function BetaDisclaimerGate() {
  const isAuthenticated = useIsAuthenticated();
  const betaDisclaimer = useBetaDisclaimer();
  const setBetaDisclaimer = useStore((state) => state.auth.setBetaDisclaimer);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mustAccept = isAuthenticated && !!betaDisclaimer && !betaDisclaimer.accepted;

  const handleAccept = async () => {
    if (!betaDisclaimer) return;

    setError(null);
    setIsSubmitting(true);
    try {
      const { betaDisclaimer: updated } = await disclaimerAPI.accept({
        version: betaDisclaimer.currentVersion,
      });
      setBetaDisclaimer(updated);
    } catch (err) {
      console.error('Failed to record disclaimer acceptance:', err);
      setError(
        err instanceof Error ? err.message : 'Could not record your acceptance. Please try again.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!mustAccept) return null;

  return (
    <BetaDisclaimerModal
      isOpen={mustAccept}
      content={betaDisclaimer.content}
      isSubmitting={isSubmitting}
      error={error}
      onAccept={handleAccept}
    />
  );
}
